import { useEffect, useRef, useState } from "react";
import { signInWithPopup, signOut } from "firebase/auth";
import { api, ApiError } from "../api/client";
import { useHold } from "../fresh";
import { firebaseAuth, firebaseConfigured, googleProvider } from "../lib/firebase";
import { bootOff, bootShow, bootUp } from "../bootScreen";
import type { Gate3D } from "../gate3d";
import type { Me } from "../api/types";
import { ShellIcon } from "./ShellIcons";
import "../gate.css";

interface Props {
  onSignedIn: (me: Me) => void;
  /** Why the previous session ended, if it did (e.g. an expired session). */
  notice?: string;
}

/**
 * Sign-in is Google, and only Google.
 *
 * The console can read every customer conversation, and it used to be guarded by
 * a password it had no way to change: a forgotten one meant editing the database
 * by hand, and a reused one is somebody else's breach away from being ours.
 * Google proves who someone is; the API decides whether they may be here, which
 * is a row keyed on their address and nothing else.
 *
 * The page is production's (the seal on the brand's teal, the serif 欢迎回来 dated like a
 * letter, a paper side with the LZ blind-embossed in its corner, the phone's sheet) with
 * prototype v2.20's additions: the seal in 3D gold, gold dust in the lamplight, a lamp over
 * the paper that follows the pointer, 欢迎回来 inked in letter by letter, the greeting by the
 * hour, a message coloured by how bad it is — and signing in, the form leaves, the gold LZ
 * flies at you and the loading screen comes over it while the console opens.
 */

/** How bad a message is: red when it is refused, amber when something is in the way. */
type Tone = "bad" | "warn";

const REDUCE = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const gate3dModule = () => import("../gate3d").catch(() => null);

export default function LoginGate({ onSignedIn, notice }: Props) {
  const [err, setErr] = useState<{ text: string; tone: Tone } | null>(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // The page's entrance plays as the loading screen goes, not underneath it, unseen.
  const [go, setGo] = useState(() => !bootUp());
  const art = useRef<HTMLElement>(null);
  const side = useRef<HTMLElement>(null);
  const lampRef = useRef<HTMLElement>(null);
  const g3 = useRef<Gate3D | null>(null);
  const want3d = useRef(false);
  // On a phone Google's window is another tab, and this one is hidden while it
  // is open: a reload for a new build then would lose the sign-in (fresh.tsx).
  useHold(busy || leaving);

  useEffect(() => {
    loadSerif();
    let alive = true;
    let stopDust = () => {};
    let released = false;
    // The loading screen (when there is one) stays until the page is ready — the 3D built and
    // compiled, the serif in — so none of that work runs while the entrance plays.
    const hadBoot = bootUp();
    const release = () => {
      if (released) return;
      released = true;
      if (hadBoot) bootOff(1200);
    };
    const failSafe = window.setTimeout(release, 1600); // never more than 1.6 s more (a first visit fetching three.js comes in later)
    const fallback = () => {
      if (alive && art.current) stopDust = gateDust(art.current);
      release();
    };
    if (!REDUCE && hasWebGL())
      void gate3dModule().then((m) =>
        !m
          ? fallback()
          : m
              .loadThree()
              .then((T) => {
                if (!alive || !art.current) return;
                g3.current = m.gate3d(T, art.current, {
                  anchor: art.current.querySelector<HTMLElement>(".gate-seal"),
                  onShow: () => art.current?.classList.add("is3d"),
                  onReady: () => void Promise.race([document.fonts?.ready, new Promise((r) => window.setTimeout(r, 600))]).then(release),
                });
                if (want3d.current) g3.current.show();
              })
              .catch(fallback),
      );
    else fallback();
    // The lamp over the paper: one element moved by its transform, at most once a frame.
    let lx = 0;
    let ly = 0;
    let lraf = 0;
    let rect: DOMRect | null = null;
    const lamp = (e: PointerEvent) => {
      lx = e.clientX;
      ly = e.clientY;
      if (lraf) return;
      lraf = requestAnimationFrame(() => {
        lraf = 0;
        const sd = side.current;
        const el = lampRef.current;
        if (!sd || !el) return;
        if (!rect) rect = sd.getBoundingClientRect();
        el.style.transform = `translate3d(${Math.round(lx - rect.left)}px,${Math.round(ly - rect.top)}px,0)`;
        if (!el.classList.contains("on")) el.classList.add("on");
      });
    };
    const reRect = () => {
      rect = null;
    };
    window.addEventListener("pointermove", lamp, { passive: true });
    window.addEventListener("resize", reRect);
    return () => {
      alive = false;
      window.clearTimeout(failSafe);
      stopDust();
      cancelAnimationFrame(lraf);
      window.removeEventListener("pointermove", lamp);
      window.removeEventListener("resize", reRect);
      g3.current?.destroy();
      g3.current = null;
    };
  }, []);

  // The entrance waits for the loading screen to go.
  useEffect(() => {
    if (go) return;
    const on = () => setGo(true);
    window.addEventListener("lz-boot-gone", on);
    const t = window.setTimeout(on, 4000);
    return () => {
      window.removeEventListener("lz-boot-gone", on);
      window.clearTimeout(t);
    };
  }, [go]);
  // The 3D seal comes in over the drawn one once the entrance has played (the drawn seal has stopped moving).
  useEffect(() => {
    if (!go) return;
    const t = window.setTimeout(() => {
      want3d.current = true;
      g3.current?.show();
    }, 1250);
    return () => window.clearTimeout(t);
  }, [go]);

  async function google() {
    if (!firebaseAuth) {
      setErr({ text: "这个环境还没接好 Google 登录", tone: "bad" });
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const cred = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await cred.user.getIdToken();
      // The Firebase session has done its only job. Dropping it means a shared
      // machine is not left signed in to Google on our behalf, and the console's
      // own session token is the single thing that governs access from here.
      await signOut(firebaseAuth).catch(() => {});
      const me = await api.googleLogin(idToken);
      // In: the form leaves and the gold LZ flies at you; the loading screen comes over it, and
      // the console is built under it (building it is the heavy part).
      setLeaving(true);
      const finish = () => {
        bootShow("正在登入…");
        window.setTimeout(() => onSignedIn(me), 380);
      };
      if (g3.current) g3.current.fly(finish);
      else window.setTimeout(finish, 420);
    } catch (e) {
      const code = (e as { code?: string })?.code ?? "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setErr(null); // they changed their mind; not an error
      } else if (code === "auth/popup-blocked") {
        setErr({ text: "浏览器挡住了登录视窗，允许这个网站弹出视窗后再试一次", tone: "warn" });
      } else if (e instanceof ApiError && e.status === 403) {
        // The server names the address on purpose: picking the wrong account in
        // Google's chooser is by far the most common way this fails.
        setErr({ text: e.message || "这个 Google 账号还没有权限", tone: "bad" });
      } else if (e instanceof ApiError && e.status === 401) {
        setErr({ text: "Google 登录没能确认，请再试一次", tone: "bad" });
      } else {
        setErr({ text: e instanceof Error ? e.message : "登录不了，请再试一次", tone: "bad" });
      }
      setBusy(false);
    }
  }

  // An expired session or a sign-out in another tab is said in amber: nothing is wrong with this one.
  const msg: { text: string; tone: Tone } | null = !firebaseConfigured
    ? { text: "这个环境还没接好 Google 登录", tone: "bad" }
    : (err ?? (notice ? { text: notice, tone: "warn" } : null));

  // The brand's own colours and the logo the owner sent (2026-09-23), not the
  // console's theme: the first screen is the one that should look like the
  // company, in either theme.
  return (
    <div className={`gate${go ? " go" : ""}${leaving ? " leaving" : ""}`}>
      <section className="gate-art" ref={art} aria-hidden="true">
        <canvas className="gate-dust" />
        <div className="gate-art-inner">
          <span className="gate-seal">
            <i className="gate-mark" />
          </span>
          <i className="gate-word" />
          <span className="gate-crm">CRM</span>
        </div>
        <p className="gate-tag">{TAGLINE}</p>
      </section>

      <main className="gate-side" ref={side}>
        <i className="gate-emboss" aria-hidden="true" />
        <i className="gate-lamp" ref={lampRef} aria-hidden="true" />
        <div className="gate-card">
          <i className="gate-mini" aria-hidden="true" />
          <p className="gate-date">
            {today()} · {hello()}
          </p>
          <h1 className="gate-h" aria-label={HEADLINE}>
            {[...HEADLINE].map((c, i) => (
              <span key={i} style={{ ["--i" as string]: i }} aria-hidden="true">
                {c}
              </span>
            ))}
          </h1>
          <p className="gate-sub">用公司的 Google 账号登录</p>

          {/* Google's own mark and proportions. A flat coloured rectangle saying
              "Sign in with Google" is the shape of a phishing page; the real
              button is the one people have been taught to trust. */}
          <button className="gsi" onClick={google} disabled={busy || leaving || !firebaseConfigured} aria-busy={busy}>
            {busy ? <i className="gate-spin" aria-hidden="true" /> : <GoogleMark />}
            <span>{busy ? "正在登录…" : "使用 Google 账号登录"}</span>
          </button>

          <p className={`gate-msg${msg ? ` ${msg.tone}` : ""}`} role="alert">
            {msg && (
              <>
                <ShellIcon name={msg.tone === "bad" ? "alert" : "info"} size={15} />
                <span>{msg.text}</span>
              </>
            )}
          </p>

          <p className="gate-foot">没有权限？请管理员在「团队」里加上你的邮箱</p>
        </div>
        <footer className="gate-copy">© {new Date().getFullYear()} LaZior</footer>
      </main>
    </div>
  );
}

const HEADLINE = "欢迎回来";
const TAGLINE = "客户、对话、成交，一目了然";

/**
 * The two lines above set in Noto Serif SC, the serif that goes with the
 * wordmark. Google Fonts cuts the font to exactly their characters (about 7 KB),
 * so a changed line must stay in this request or it falls back to the system
 * serif. Only a signed-out visitor ever asks for it.
 */
function loadSerif() {
  if (document.getElementById("gate-serif")) return;
  const link = document.createElement("link");
  link.id = "gate-serif";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@500;600&display=swap&text=" +
    encodeURIComponent(HEADLINE + TAGLINE);
  document.head.appendChild(link);
}

/** 「9月23日 · 星期三」, on this device's clock. */
function today(): string {
  const d = new Date();
  return `${d.getMonth() + 1}月${d.getDate()}日 · 星期${"日一二三四五六"[d.getDay()]}`;
}

/** The greeting by the hour, after the date. */
function hello(): string {
  const h = new Date().getHours();
  return h < 5 ? "晚上好" : h < 11 ? "早上好" : h < 13 ? "中午好" : h < 18 ? "下午好" : "晚上好";
}

function hasWebGL(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch {
    return false;
  }
}

/**
 * Gold dust in the lamplight, where the 3D seal cannot be drawn: specks drifting up through the brand
 * panel, each twinkling at its own pace, brighter near the lamp behind the mark.
 */
function gateDust(host: HTMLElement): () => void {
  if (REDUCE) return () => {};
  const c = host.querySelector<HTMLCanvasElement>(".gate-dust");
  const x = c?.getContext("2d");
  if (!c || !x) return () => {};
  const sp = document.createElement("canvas");
  sp.width = sp.height = 64;
  {
    const g = sp.getContext("2d")!;
    const r = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    r.addColorStop(0, "rgba(255,240,205,1)");
    r.addColorStop(0.28, "rgba(232,208,150,.55)");
    r.addColorStop(1, "rgba(220,190,120,0)");
    g.fillStyle = r;
    g.fillRect(0, 0, 64, 64);
  }
  let w = 0;
  let h = 0;
  let raf = 0;
  let last = performance.now();
  const fit = () => {
    w = host.clientWidth;
    h = host.clientHeight;
    c.width = w;
    c.height = h;
  };
  fit();
  const ro = new ResizeObserver(fit);
  ro.observe(host);
  const ps = Array.from({ length: 72 }, () => ({
    x: Math.random(),
    y: Math.random(),
    r: 2 + Math.random() * 5,
    v: 0.008 + Math.random() * 0.02,
    dx: (Math.random() - 0.5) * 0.008,
    ph: Math.random() * 6.28,
    tw: 0.5 + Math.random() * 1.8,
  }));
  const loop = (now: number) => {
    raf = requestAnimationFrame(loop);
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    x.clearRect(0, 0, w, h);
    x.globalCompositeOperation = "lighter";
    for (const p of ps) {
      p.y -= p.v * dt;
      p.x += p.dx * dt;
      p.ph += p.tw * dt;
      if (p.y < -0.03) {
        p.y = 1.03;
        p.x = Math.random();
      }
      const near = Math.max(0, 1 - Math.hypot(p.x - 0.5, (p.y - 0.42) * 1.2) * 1.6);
      x.globalAlpha = (0.18 + 0.5 * (0.5 + 0.5 * Math.sin(p.ph))) * (0.45 + 0.55 * near);
      x.drawImage(sp, p.x * w - p.r, p.y * h - p.r, p.r * 2, p.r * 2);
    }
    x.globalAlpha = 1;
  };
  raf = requestAnimationFrame(loop);
  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
  };
}

/** Google's four-colour G, at the size their guidelines put beside the label. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
